import React, { useEffect, useId, useState } from 'react';
import { GoalBuildUp } from '@/services/api';
import { formatZone, formatAction } from './helpers';
import PatternBreadcrumb from './PatternBreadcrumb';
import GoalSequenceTimeline from './GoalSequenceTimeline';
import BuildUpPitch from './BuildUpPitch';

interface Props {
  goal: GoalBuildUp;
  index: number;
  initiallyExpanded?: boolean;
}

const GoalCard: React.FC<Props> = ({ goal, index, initiallyExpanded }) => {
  const [showSequence, setShowSequence] = useState(initiallyExpanded ?? index === 0);
  const controlId = useId();
  const regionId = useId();

  useEffect(() => {
    setShowSequence(initiallyExpanded ?? index === 0);
  }, [index, initiallyExpanded]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Goal header */}
      <div
        className="p-4 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-750"
        onClick={() => setShowSequence(!showSequence)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowSequence(!showSequence); } }}
        role="button"
        tabIndex={0}
        id={controlId}
        aria-expanded={showSequence}
        aria-controls={regionId}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-sm font-bold">
              {goal.minute}'
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {goal.scorer?.name || 'Unknown'}
                </span>
                {goal.isOwnGoal && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">OG</span>
                )}
                {goal.setPieceType && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400">
                    {formatAction(goal.setPieceType)}
                  </span>
                )}
                <span className="text-xs text-gray-500 dark:text-gray-400">xG: {goal.xg.toFixed(2)}</span>
                {goal.bodyPart && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">({formatAction(goal.bodyPart)})</span>
                )}
              </div>
              <div className="mt-1">
                <PatternBreadcrumb pattern={goal.patternSummary} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {goal.sequenceLength} events
            </span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${showSequence ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {/* Meta badges */}
        <div className="flex flex-wrap gap-2 mt-2 ml-13">
          {goal.startZone && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
              Start: {formatZone(goal.startZone)}
            </span>
          )}
          {goal.flank && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400">
              Flank: {goal.flank}
            </span>
          )}
          {goal.duration && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              Duration: {goal.duration}s
            </span>
          )}
        </div>
      </div>

      {/* Expanded content: pitch + timeline */}
      {showSequence && (
        <div id={regionId} role="region" aria-labelledby={controlId} className="p-4 border-t border-gray-200 dark:border-gray-700">
          {(() => {
            const hasLeadUp = goal.leadUp && goal.leadUp.length > 0;
            const pitchSequence = hasLeadUp ? [...goal.leadUp!, ...goal.sequence] : goal.sequence;
            const hasLocations = pitchSequence.some(e => e.location);
            return hasLocations ? (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="md:col-span-2">
                  <BuildUpPitch sequence={pitchSequence} />
                </div>
                <div className="md:col-span-3">
                  {hasLeadUp && (
                    <>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Build-up to {formatAction(goal.setPieceType || 'set piece')}:
                      </p>
                      <GoalSequenceTimeline sequence={goal.leadUp!} />
                      <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-3" />
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {formatAction(goal.setPieceType || 'Set piece')} delivery:
                      </p>
                    </>
                  )}
                  <GoalSequenceTimeline sequence={goal.sequence} />
                </div>
              </div>
            ) : (
              <>
                {hasLeadUp && (
                  <>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Build-up to {formatAction(goal.setPieceType || 'set piece')}:
                    </p>
                    <GoalSequenceTimeline sequence={goal.leadUp!} />
                    <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-3" />
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {formatAction(goal.setPieceType || 'Set piece')} delivery:
                    </p>
                  </>
                )}
                <GoalSequenceTimeline sequence={goal.sequence} />
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default GoalCard;
